/** The strip of tabs under a place - **level one of at most two** (ADR-0212; v5 stage F1).
 *
 * The places with more than one screen draw their screens here, from `routeTable.ts`, for the role
 * looking. A screen's own controls (a record's five tabs, Help's three) are the second level and the
 * last: nothing nests a third. The strip is one visual (`components/ui/SectionTabs`); this file only
 * says which tabs there are and where each goes.
 *
 * A tab is a path, or a path and `?tab=` where the tabs are views of one screen (`tabKey`). The
 * first tab of a path carries no parameter - one URL for the default view, as `useUrlTab` has it -
 * and a link to it **clears** `tab`, so a `tab=missing` carried from another tab cannot stay on a
 * tab that no longer means it.
 *
 * **These are links and not a tablist**, because each tab is a different URL (see the component).
 * It draws nothing for a place with one tab, and nothing for a role that is offered one: the tabs a
 * role cannot use are not drawn (the company Admin's, in Settings, are the clearest case); a screen
 * reached by link that is not one of the role's tabs simply lights none.
 */

import { Link } from "@tanstack/react-router";

import { SectionTabs as Strip } from "../components/ui/SectionTabs";
import { locate, tabKey, tabsFor } from "./routeTable";
import type { RecordPlace } from "./routeTable";

export function SectionTabs({
  pathname,
  tab,
  role,
  carry,
  lights,
}: {
  /** A page's own answer for a path that serves many places (a record). */
  lights?: RecordPlace;
  pathname: string;
  /** The `?tab=` value in the address, which tells two tabs of one path apart. */
  tab: string | undefined;
  role: string | undefined;
  /** The search parameters that travel between screens (the window and the Scope 2 method). */
  carry: (prev: Record<string, unknown>) => Record<string, unknown>;
}) {
  const here = locate(pathname, tab, lights);
  if (!here) return null;
  const places = tabsFor(here.nav.id, role);
  return (
    <Strip
      label={`${here.nav.label} pages`}
      testId="section-tabs"
      tabs={places.map((place) => ({
        key: tabKey(place),
        label: place.tab ?? place.label,
        current: here.tab === tabKey(place),
      }))}
      renderLink={(item, props) => {
        const place = places.find((candidate) => tabKey(candidate) === item.key);
        if (!place) return null;
        return (
          <Link
            to={place.to}
            search={(prev: Record<string, unknown>) => ({ ...carry(prev), tab: place.param })}
            {...props}
          />
        );
      }}
    />
  );
}
